import type {
  QueueOperation,
  QueueSnapshot,
  UploadChunkOperation,
} from "../types";
import type { QueueStore } from "./queue-store";

const RETRIABLE_STATUS_CODES = new Set([408, 429]);

export function retryDelayMs(retryCount: number): number {
  const seconds = [1, 2, 4, 8, 16][retryCount] ?? 30;
  return seconds * 1_000;
}

function isRetriableStatus(status: number): boolean {
  return RETRIABLE_STATUS_CODES.has(status) || status >= 500;
}

function sortOperations(operations: QueueOperation[]): QueueOperation[] {
  return [...operations].sort(
    (left, right) => left.order - right.order || left.id.localeCompare(right.id),
  );
}

function chunkHeaders(operation: UploadChunkOperation): Headers {
  const { metadata } = operation;
  return new Headers({
    "Content-Type": metadata.contentType,
    "X-Sequence-Number": String(metadata.sequenceNumber),
    "X-Started-At": metadata.startedAt,
    "X-Ended-At": metadata.endedAt,
    "X-Duration-Ms": String(metadata.durationMs),
    "X-Speech-Ms": String(metadata.speechMs),
    "X-Peak-Rms": String(metadata.peakRms),
    "X-Average-Rms": String(metadata.averageRms),
    "X-Likely-Silent": String(metadata.likelySilent),
  });
}

export interface UploadQueueOptions {
  store: QueueStore;
  getToken: () => string;
  fetcher?: typeof fetch;
  now?: () => number;
  requestTimeoutMs?: number;
  schedule?: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
}

type ProcessResult =
  | "success"
  | "idle"
  | "busy"
  | "scheduled"
  | "waiting"
  | "blocked";

export class UploadQueue {
  private readonly store: QueueStore;
  private readonly getToken: () => string;
  private readonly fetcher: typeof fetch;
  private readonly now: () => number;
  private readonly requestTimeoutMs: number;
  private readonly schedule: UploadQueueOptions["schedule"];
  private processing = false;
  private runPromise: Promise<void> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private listeners = new Set<(snapshot: QueueSnapshot) => void>();
  private snapshot: QueueSnapshot = {
    totalOperations: 0,
    queuedChunks: 0,
    blockedOperations: 0,
    waitingForToken: false,
    lastStatus: "Queue ready",
    lastUploadStatus: "No upload attempted",
  };

  constructor(options: UploadQueueOptions) {
    this.store = options.store;
    this.getToken = options.getToken;
    this.fetcher =
      options.fetcher ?? globalThis.fetch.bind(globalThis);
    this.now = options.now ?? Date.now;
    this.requestTimeoutMs = options.requestTimeoutMs ?? 30_000;
    this.schedule =
      options.schedule ??
      ((callback, delayMs) => setTimeout(callback, delayMs));
  }

  getSnapshot(): QueueSnapshot {
    return this.snapshot;
  }

  subscribe(listener: (snapshot: QueueSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => this.listeners.delete(listener);
  }

  async refresh(): Promise<void> {
    await this.emit();
  }

  kick(): Promise<void> {
    if (this.runPromise) return this.runPromise;
    this.runPromise = this.runLoop().finally(() => {
      this.runPromise = null;
    });
    return this.runPromise;
  }

  async processOnce(): Promise<ProcessResult> {
    if (this.processing) return "busy";

    const operations = sortOperations(await this.store.listOperations());
    if (operations.length === 0) {
      this.snapshot = { ...this.snapshot, lastStatus: "Queue empty" };
      await this.emit();
      return "idle";
    }

    let operation = operations[0];
    const sessionState = await this.store.getSessionState(operation.sessionId);

    if (operation.kind !== "create_session" && !sessionState?.serverCreated) {
      const createOperation = operations.find(
        (candidate) =>
          candidate.sessionId === operation.sessionId &&
          candidate.kind === "create_session",
      );
      if (!createOperation) {
        await this.block(
          operation,
          "Session creation is not acknowledged and no create operation exists.",
        );
        return "blocked";
      }
      operation = createOperation;
    }

    if (operation.state === "blocked") {
      this.snapshot = {
        ...this.snapshot,
        lastStatus: "Queue blocked",
        lastError: operation.lastError,
      };
      await this.emit();
      return "blocked";
    }

    if (operation.state === "waiting_token" || !this.getToken()) {
      if (operation.state !== "waiting_token") {
        await this.store.putOperation({
          ...operation,
          state: "waiting_token",
          lastError: "Recorder token required.",
        });
      }
      this.snapshot = {
        ...this.snapshot,
        lastStatus: "Waiting for recorder token",
        lastError: undefined,
      };
      await this.emit();
      return "waiting";
    }

    if (operation.nextAttemptAt > this.now()) {
      this.scheduleRetry(operation.nextAttemptAt - this.now());
      await this.emit();
      return "scheduled";
    }

    this.processing = true;
    this.snapshot = {
      ...this.snapshot,
      activeOperation: operation.kind,
      lastStatus: `Processing ${operation.kind.replaceAll("_", " ")}`,
      lastError: undefined,
    };
    await this.emit();

    try {
      const response = await this.send(operation);
      if (response.ok) {
        if (operation.kind === "create_session") {
          const current = sessionState ?? {
            sessionId: operation.sessionId,
            serverCreated: false,
            persistedChunkCount: 0,
            likelySilentCount: 0,
          };
          await this.store.putSessionState({
            ...current,
            serverCreated: true,
          });
        }
        await this.store.deleteOperation(operation.id);
        this.snapshot = {
          ...this.snapshot,
          activeOperation: undefined,
          lastStatus:
            operation.kind === "upload_chunk"
              ? `Uploaded chunk ${operation.metadata.sequenceNumber}`
              : `${operation.kind.replaceAll("_", " ")} confirmed`,
          lastUploadStatus:
            operation.kind === "upload_chunk"
              ? `Chunk ${operation.metadata.sequenceNumber} uploaded`
              : this.snapshot.lastUploadStatus,
          lastError: undefined,
        };
        return "success";
      }

      const responseMessage = await response
        .text()
        .catch(() => `HTTP ${response.status}`);
      const message = `HTTP ${response.status}: ${responseMessage || response.statusText}`;

      if (response.status === 401) {
        await this.store.putOperation({
          ...operation,
          state: "waiting_token",
          lastError: message,
        });
        this.snapshot = {
          ...this.snapshot,
          activeOperation: undefined,
          lastStatus: "Authentication failed",
          lastUploadStatus:
            operation.kind === "upload_chunk"
              ? `Chunk ${operation.metadata.sequenceNumber} rejected: authentication failed`
              : this.snapshot.lastUploadStatus,
          lastError: "Recorder token was rejected.",
        };
        return "waiting";
      }

      if (isRetriableStatus(response.status)) {
        await this.retry(operation, message);
        return "scheduled";
      }

      await this.block(operation, message);
      return "blocked";
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unknown network error";
      console.error({
        sessionId: operation.sessionId,
        chunkId:
          operation.kind === "upload_chunk"
            ? operation.metadata.chunkId
            : undefined,
        sequenceNumber:
          operation.kind === "upload_chunk"
            ? operation.metadata.sequenceNumber
            : undefined,
        operation: operation.kind,
        message,
      });
      await this.retry(operation, message);
      return "scheduled";
    } finally {
      this.processing = false;
      await this.emit();
    }
  }

  async notifyTokenChanged(): Promise<void> {
    const operations = await this.store.listOperations();
    await Promise.all(
      operations
        .filter((operation) => operation.state === "waiting_token")
        .map((operation) =>
          this.store.putOperation({
            ...operation,
            state: "pending",
            nextAttemptAt: 0,
            lastError: undefined,
          }),
        ),
    );
    await this.kick();
  }

  async retryBlocked(): Promise<void> {
    const operations = await this.store.listOperations();
    await Promise.all(
      operations
        .filter((operation) => operation.state === "blocked")
        .map((operation) =>
          this.store.putOperation({
            ...operation,
            state: "pending",
            nextAttemptAt: 0,
            lastError: undefined,
          }),
        ),
    );
    await this.kick();
  }

  async retryPendingNow(): Promise<void> {
    const operations = await this.store.listOperations();
    await Promise.all(
      operations
        .filter(
          (operation) =>
            operation.state === "pending" &&
            operation.nextAttemptAt > this.now(),
        )
        .map((operation) =>
          this.store.putOperation({
            ...operation,
            nextAttemptAt: 0,
          }),
        ),
    );
    await this.kick();
  }

  private async runLoop(): Promise<void> {
    while (true) {
      const result = await this.processOnce();
      if (result !== "success") return;
    }
  }

  private async send(operation: QueueOperation): Promise<Response> {
    const token = this.getToken();
    const controller = new AbortController();
    const timeout = setTimeout(
      () => controller.abort(new DOMException("Upload timed out", "AbortError")),
      this.requestTimeoutMs,
    );
    const authHeaders = { Authorization: `Bearer ${token}` };

    let input: RequestInfo | URL;
    let init: RequestInit;

    switch (operation.kind) {
      case "create_session":
        input = "/api/sessions";
        init = {
          method: "POST",
          headers: {
            ...authHeaders,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(operation.payload),
        };
        break;
      case "create_event":
        input = `/api/sessions/${encodeURIComponent(operation.sessionId)}/events`;
        init = {
          method: "POST",
          headers: {
            ...authHeaders,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(operation.payload),
        };
        break;
      case "complete_session":
        input = `/api/sessions/${encodeURIComponent(operation.sessionId)}/complete`;
        init = {
          method: "POST",
          headers: {
            ...authHeaders,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(operation.payload),
        };
        break;
      case "upload_chunk": {
        const headers = chunkHeaders(operation);
        headers.set("Authorization", `Bearer ${token}`);
        input = `/api/sessions/${encodeURIComponent(operation.sessionId)}/chunks/${encodeURIComponent(operation.metadata.chunkId)}`;
        init = {
          method: "PUT",
          headers,
          body: operation.blob,
        };
        break;
      }
    }

    try {
      return await this.fetcher(input, { ...init, signal: controller.signal });
    } finally {
      clearTimeout(timeout);
    }
  }

  private async retry(
    operation: QueueOperation,
    message: string,
  ): Promise<void> {
    const delay = retryDelayMs(operation.retryCount);
    await this.store.putOperation({
      ...operation,
      retryCount: operation.retryCount + 1,
      nextAttemptAt: this.now() + delay,
      state: "pending",
      lastError: message,
    });
    this.snapshot = {
      ...this.snapshot,
      activeOperation: undefined,
      lastStatus: `Retrying in ${delay / 1_000}s`,
      lastUploadStatus:
        operation.kind === "upload_chunk"
          ? `Chunk ${operation.metadata.sequenceNumber} failed; retry scheduled`
          : this.snapshot.lastUploadStatus,
      lastError: message,
    };
    this.scheduleRetry(delay);
  }

  private async block(
    operation: QueueOperation,
    message: string,
  ): Promise<void> {
    await this.store.putOperation({
      ...operation,
      state: "blocked",
      lastError: message,
    });
    this.snapshot = {
      ...this.snapshot,
      activeOperation: undefined,
      lastStatus: "Queue blocked",
      lastUploadStatus:
        operation.kind === "upload_chunk"
          ? `Chunk ${operation.metadata.sequenceNumber} blocked`
          : this.snapshot.lastUploadStatus,
      lastError: message,
    };
  }

  private scheduleRetry(delayMs: number): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = this.schedule?.(() => {
      this.retryTimer = undefined;
      void this.kick();
    }, Math.max(0, delayMs));
  }

  private async emit(): Promise<void> {
    const operations = await this.store.listOperations();
    this.snapshot = {
      ...this.snapshot,
      totalOperations: operations.length,
      queuedChunks: operations.filter(
        (operation) => operation.kind === "upload_chunk",
      ).length,
      blockedOperations: operations.filter(
        (operation) => operation.state === "blocked",
      ).length,
      waitingForToken: operations.some(
        (operation) => operation.state === "waiting_token",
      ),
    };
    for (const listener of this.listeners) listener(this.snapshot);
  }
}
